import { auth } from '../firebase/config';
import {
  getAuditActionGroup,
  getAuditActionLabel,
  getAuditTargetTypeLabel,
  getAuditUserTypeLabel,
  resolveAuditUserType,
} from '../utils/auditPresentation';
import { getAuditLocationPresentation } from '../utils/auditLocationPresentation';

const AUDIT_API_PATH = '/api/ieum-audit';
const DEFAULT_LIMIT = 300;

const authorizedFetch = async (url, init = {}, forceRefresh = false) => {
  const firebaseUser = auth.currentUser;
  if (!firebaseUser) throw new Error('로그인된 사용자가 필요합니다.');
  const headers = new Headers(init.headers || {});
  headers.set('Authorization', `Bearer ${await firebaseUser.getIdToken(forceRefresh)}`);
  headers.set('Accept', 'application/json');
  const response = await fetch(url, {
    ...init,
    headers,
    credentials: 'same-origin',
    cache: 'no-store',
  });
  if (response.status === 401 && !forceRefresh) return authorizedFetch(url, init, true);
  return response;
};

const parseResponse = async (response) => {
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload?.error || `감사 API 요청 실패 (${response.status})`);
  return payload?.data ?? payload;
};

const toDate = (value) => {
  const date = value instanceof Date ? value : new Date(value || 0);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeAuditLog = (row = {}) => {
  const actor = row.actor && typeof row.actor === 'object' ? row.actor : {};
  const details = row.details && typeof row.details === 'object' ? row.details : {};
  const request = row.request && typeof row.request === 'object' ? row.request : {};
  const scopeId = String(row.realtorId || '');
  const service = scopeId.startsWith('ieum-app:') ? 'ieum' : 'realtorauto';
  const rawAction = String(row.action || '');
  const rawUserType = String(actor.role || '');
  const targetType = String(row.targetType || '');
  const userType = resolveAuditUserType({
    role: rawUserType,
    actor,
    action: rawAction,
    source: row.source,
  });
  const actionLabel = getAuditActionLabel({
    action: rawAction,
    targetType,
    targetId: row.targetId,
    summary: row.summary,
  });
  const location = getAuditLocationPresentation(request);
  return {
    id: row.id || '',
    date: toDate(row.createdAt),
    createdAt: row.createdAt || '',
    user: actor.displayName || actor.email || actor.uid || (userType === 'guest' ? '비로그인' : '시스템'),
    userType,
    userTypeLabel: getAuditUserTypeLabel(userType),
    rawUserType: rawUserType || '-',
    action: rawAction || '-',
    actionLabel,
    actionGroup: getAuditActionGroup(rawAction),
    target: targetType || '-',
    targetType: targetType || '-',
    targetLabel: getAuditTargetTypeLabel(targetType),
    targetId: row.targetId || '',
    networkLocation: location.summary,
    networkLocationDetail: location.detail,
    networkLocationPrecision: request.networkLocationPrecision || 'unknown',
    ipLast4: request.ipLast4 || '-',
    accessEnvironment: request.accessEnvironment || '기존 기록(미수집)',
    result: row.result || 'INFO',
    summary: row.summary || '',
    service,
    serviceLabel: service === 'ieum' ? '이음 운영' : '부동산 오토',
    scopeId,
    contractId: row.contractId || '',
    source: row.source || '',
    meta: {
      ...details,
      request: {
        ...request,
        networkLocation: location.summary,
        networkLocationDetail: location.detail,
      },
      auditScope: scopeId,
      contractId: row.contractId || '',
      source: row.source || '',
      rawAction: rawAction || '-',
      rawUserType: rawUserType || '-',
    },
  };
};

export const recordAuditLogAidm = async ({
  action,
  actorRole = null,
  targetType = null,
  targetId = null,
  before = null,
  after = null,
  meta = null,
  accessLocation = null,
} = {}) => {
  if (!action) return null;
  const locationMeta = meta && typeof meta === 'object' ? meta : {};
  const response = await authorizedFetch(AUDIT_API_PATH, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    keepalive: true,
    body: JSON.stringify({
      action: String(action).slice(0, 160),
      targetType: targetType ? String(targetType).slice(0, 120) : null,
      targetId: targetId ? String(targetId).slice(0, 200) : null,
      result: 'INFO',
      summary: String(action).slice(0, 240),
      pagePath: locationMeta.path || '',
      pageUrl: locationMeta.href || '',
      accessLocation,
      details: { actorRole, before, after, meta: locationMeta },
    }),
  });
  return parseResponse(response);
};

export const listAuditLogsAidm = async ({ limit = DEFAULT_LIMIT, days = 30 } = {}) => {
  const params = new URLSearchParams({
    limit: String(Number(limit) || DEFAULT_LIMIT),
    days: String(Number(days) || 30),
  });
  const response = await authorizedFetch(`${AUDIT_API_PATH}?${params.toString()}`);
  const data = await parseResponse(response);
  const entries = Array.isArray(data?.entries) ? data.entries : [];
  return entries.map(normalizeAuditLog);
};
